import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  Ref,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Map, { Source, Layer, Marker, MapRef } from "react-map-gl";
import { toast } from "react-toastify";
import mapboxgl from "mapbox-gl";

import { getStopsByCurrentSemanticMap } from "Slices/stops";
import {
  getVehiclesUpdateDataArray,
  getVehiclesUpdateData,
} from "Slices/vehiclesState";
import { getVehiclesDict } from "Slices/vehicles/selectors";
import {
  getActiveTooltipVehicleId,
  setActiveTooltipVehicleId,
} from "Slices/appState";
import { convertVehicleData, isCoordinatesValid } from "Utils";
import { FMSLogger } from "FMSLogger";

const mapComponentLogger = FMSLogger.byPrefix("map-component");

import { ReactComponent as StopIcon } from "./assets/stop.svg";
import { ReactComponent as PressedStopIcon } from "./assets/pressedStop.svg";
import { ReactComponent as StopAverageIcon } from "./assets/groupStops.svg";
import { VehicleIcon } from "./components/VehicleIcon";
import { isTelemetryFresh } from "./components/VehicleIcon/telemetryFreshness";
import { VehicleTooltip } from "./components/VehicleTooltip";
import { RouteLayer } from "./layers";
import {
  StopLabelStyled,
  StopAverageStyled,
  MarkerStyles,
  VehicleLabelStyled,
} from "./styles";

import "mapbox-gl/dist/mapbox-gl.css";

const TOKEN =
  "pk.eyJ1IjoiZHJldWNlIiwiYSI6ImNsZmw5MjBjeTAydDczenJyZGNwYzNuM2oifQ.OEG4BvbK2VarceCidetCyQ";

type MapBoundsType = [number, number, number, number];

interface MapComponentInterface {
  currentSemanticMap: SemanticMap | null;
  mapBounds?: MapBoundsType;
  showSatView: boolean;
  pressedStopId?: string | null;
  hoveredVehicleId?: string | null;
  focusedVehicleIds?: Set<string>;
}

const DEFAULT_SOURCE = {
  type: "FeatureCollection",
  features: [],
};

const averageStop = (stops: Array<Stop>) => {
  let lat = 0;
  let long = 0;
  stops.forEach(({ latitude, longitude }) => {
    if (latitude && longitude) {
      lat = lat + latitude;
      long = long + longitude;
    }
  });

  return {
    averageLatitude: lat === 0 ? 0 : lat / stops.length,
    averageLongitude: long == 0 ? 0 : long / stops.length,
  };
};

export const MapComponent = ({
  currentSemanticMap,
  mapBounds,
  showSatView,
  pressedStopId,
  hoveredVehicleId,
  focusedVehicleIds,
}: MapComponentInterface) => {
  const dispatch = useDispatch();
  const vehiclesUpdateData = useSelector(getVehiclesUpdateDataArray);
  const vehiclesUpdateDataById = useSelector(getVehiclesUpdateData);
  const vehiclesDict = useSelector(getVehiclesDict);
  const stops: Array<Stop> = useSelector(getStopsByCurrentSemanticMap);
  const activeTooltipVehicleId = useSelector(getActiveTooltipVehicleId);
  const [showStops, setShowStops] = useState<boolean>(false);
  const [routeSrc, setRouteSrc] = useState<any>(DEFAULT_SOURCE);
  const mapRef = useRef<mapboxgl.Map | null>();
  const { averageLongitude, averageLatitude } = useMemo(
    () => averageStop(stops),
    [stops]
  );

  const isStopsCoordinatesValid = useMemo(
    () =>
      stops.every((stop) => isCoordinatesValid(stop.longitude, stop.latitude)),
    [stops]
  );

  useEffect(() => {
    setTimeout(() => {
      if (mapBounds && mapRef.current) {
        mapRef.current.fitBounds(mapBounds, {
          duration: 2000,
          maxZoom: 20,
          linear: false,
          offset: [80, 0],
        });
      }
    }, 500);
  }, [mapBounds]);

  useEffect(() => {
    if (currentSemanticMap && currentSemanticMap.track) {
      const isRoutesCoordinatesValid =
        currentSemanticMap.track.features?.[0]?.geometry?.coordinates?.every(
          (coordinate: Array<string>) =>
            isCoordinatesValid(Number(coordinate[0]), Number(coordinate[1]))
        );
      if (isRoutesCoordinatesValid) {
        setRouteSrc(currentSemanticMap.track);
      } else {
        toast.error("Incorrect zone coordinates!");
      }
    }
  }, [currentSemanticMap]);

  const onMapLoad = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.on("zoom", () => {
        const currentZoom = mapRef.current?.getZoom();
        if (currentZoom && currentZoom >= 17) {
          setShowStops(true);
        }

        if (currentZoom && currentZoom < 17) {
          setShowStops(false);
        }
      });
    }
  }, [showStops, mapRef, mapRef.current]);

  const showVehicleTooltip = useCallback(
    (id: string) => {
      if (activeTooltipVehicleId && activeTooltipVehicleId === id) {
        dispatch(setActiveTooltipVehicleId(null));
        return;
      }

      dispatch(setActiveTooltipVehicleId(id));
    },
    [activeTooltipVehicleId, vehiclesUpdateData, dispatch]
  );

  //VEHICLES
  const vehicleItems = useMemo(() => {
    if (!vehiclesUpdateData) {
      return [];
    }

    const vehicleMarkers: Array<JSX.Element> = [];
    const { averageLatitude: originLat, averageLongitude: originLng } =
      averageStop(stops);

    vehiclesUpdateData.forEach(
      ({
        t_long,
        t_lat,
        vehicle_id,
        t_virtual_bumper_status,
        t_web_status,
        online_status,
        received_at,
      }) => {
        const currentVehicleData = vehiclesDict[vehicle_id];
        const hasTelemetryCoords = Boolean(t_long && t_lat);
        const hasLastKnownCoords = Boolean(
          currentVehicleData?.longitude && currentVehicleData?.latitude
        );
        const longitude = t_long || currentVehicleData?.longitude || originLng;
        const latitude = t_lat || currentVehicleData?.latitude || originLat;

        if (
          vehicle_id &&
          !hasTelemetryCoords &&
          !hasLastKnownCoords &&
          latitude &&
          longitude
        ) {
          mapComponentLogger.warn(
            `Vehicle ${vehicle_id} missing telemetry and last-known coords; falling back to stop-average origin`
          );
        }

        if (vehicle_id && latitude && longitude) {
          const isHovered = hoveredVehicleId === vehicle_id;
          const isFocused = focusedVehicleIds?.has(vehicle_id);

          vehicleMarkers.push(
            <Marker
              key={vehicle_id}
              longitude={longitude}
              latitude={latitude}
              scale={5}
              anchor={"top-left"}
              offset={[-6, -3]}
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                showVehicleTooltip(vehicle_id);
              }}
              style={{
                ...MarkerStyles,
                zIndex: 2,
                cursor: "pointer",
              }}
            >
              <VehicleIcon
                vbStatus={
                  t_virtual_bumper_status || currentVehicleData?.vbStatus
                }
                webstate={t_web_status || currentVehicleData?.webstate}
                isHovered={isHovered}
                isFocused={isFocused}
                onlineStatus={online_status}
                hasLiveTelemetry={
                  !!t_web_status && isTelemetryFresh(received_at)
                }
              />
            </Marker>
          );
        }
      }
    );

    return vehicleMarkers;
  }, [
    vehiclesUpdateData,
    activeTooltipVehicleId,
    hoveredVehicleId,
    focusedVehicleIds,
  ]);

  //STOPS
  const stopItems = useMemo(() => {
    if (!isStopsCoordinatesValid && stops.length) {
      toast.error("Incorrect stop coordinates!");
      return null;
    }
    return stops.map(({ longitude, latitude, id, name }) => {
      if (!id) {
        return null;
      }

      return (
        <Marker
          key={id}
          longitude={longitude}
          latitude={latitude}
          onClick={(e) => {
            e.originalEvent.stopPropagation();
          }}
          anchor={"center"}
          style={MarkerStyles}
        >
          <StopLabelStyled>{name}</StopLabelStyled>
          {id === pressedStopId ? <PressedStopIcon /> : <StopIcon />}
        </Marker>
      );
    });
  }, [stops, pressedStopId, isStopsCoordinatesValid]);

  //STOPS COVER
  const stopAverage = useMemo(
    () => (
      <StopAverageStyled>
        {isStopsCoordinatesValid && (
          <Marker
            longitude={averageLongitude}
            latitude={averageLatitude}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
            }}
            anchor={"center"}
            offset={[0, -8]}
            style={{ ...MarkerStyles, zIndex: 3 }}
          >
            <StopLabelStyled center={true}>{stops.length}</StopLabelStyled>
            <StopAverageIcon />
          </Marker>
        )}
      </StopAverageStyled>
    ),
    [stops, isStopsCoordinatesValid]
  );

  return (
    <Map
      ref={mapRef as Ref<MapRef> | undefined}
      initialViewState={{
        bounds: mapBounds,
        zoom: 0,
        bearing: 0,
        pitch: 0,
      }}
      onLoad={onMapLoad}
      mapStyle={`mapbox://styles/mapbox/${
        showSatView ? "satellite" : "light"
      }-v9`}
      mapboxAccessToken={TOKEN}
    >
      {activeTooltipVehicleId && vehiclesUpdateDataById && (
        <VehicleTooltip
          {...convertVehicleData(
            vehiclesUpdateDataById[activeTooltipVehicleId]
          )}
          vehicleData={vehiclesDict[activeTooltipVehicleId]}
          closeAction={() => dispatch(setActiveTooltipVehicleId(null))}
        />
      )}
      <Source id="routeSrc" type="geojson" data={routeSrc}>
        <Layer {...RouteLayer} />
      </Source>
      {showStops && stopItems}
      {vehicleItems}
      {/* Render labels for all focused vehicles (persistent) */}
      {focusedVehicleIds &&
        Array.from(focusedVehicleIds).map((vehicleId) => {
          const vehicleUpdateData = vehiclesUpdateDataById?.[vehicleId];
          const vehicleStaticData = vehiclesDict[vehicleId];
          if (!vehicleStaticData) return null;

          // Use update data coordinates with fallback to static vehicle data
          const longitude =
            vehicleUpdateData?.t_long ?? vehicleStaticData?.longitude;
          const latitude =
            vehicleUpdateData?.t_lat ?? vehicleStaticData?.latitude;

          if (longitude == null || latitude == null) return null;

          return (
            <Marker
              key={`label-focused-${vehicleId}`}
              longitude={longitude}
              latitude={latitude}
              anchor="top"
              offset={[0, -40]}
              style={{ ...MarkerStyles, zIndex: 21, pointerEvents: "none" }}
            >
              <VehicleLabelStyled isFocused={true}>
                {vehicleStaticData.name}
              </VehicleLabelStyled>
            </Marker>
          );
        })}
      {/* Render tooltip for hovered vehicle (triggered by sidebar hover) */}
      {hoveredVehicleId &&
        !focusedVehicleIds?.has(hoveredVehicleId) &&
        vehiclesDict[hoveredVehicleId] &&
        (() => {
          const vehicleUpdateData = vehiclesUpdateDataById?.[hoveredVehicleId];
          const vehicleStaticData = vehiclesDict[hoveredVehicleId];

          // Build tooltip data with fallbacks from static vehicle data
          const tooltipData = vehicleUpdateData
            ? convertVehicleData(vehicleUpdateData)
            : {
                id: hoveredVehicleId,
                longitude: vehicleStaticData?.longitude ?? null,
                latitude: vehicleStaticData?.latitude ?? null,
                driveMode: vehicleStaticData?.driveMode ?? null,
                lastContact: vehicleStaticData?.lastContact ?? null,
                online: null,
                missionStatus: null,
              };

          // Ensure we have coordinates
          if (tooltipData.longitude == null || tooltipData.latitude == null) {
            tooltipData.longitude = vehicleStaticData?.longitude ?? null;
            tooltipData.latitude = vehicleStaticData?.latitude ?? null;
          }

          if (tooltipData.longitude == null || tooltipData.latitude == null)
            return null;

          return (
            <VehicleTooltip
              key={`tooltip-hover-${hoveredVehicleId}`}
              {...tooltipData}
              vehicleData={vehicleStaticData}
              closeAction={() => {
                console.debug("no-op");
              }} // No-op: hover is controlled by mouse events
            />
          );
        })()}
      {!showStops && stops.length && stopAverage}
    </Map>
  );
};
